import { EditorView, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import { EditorState, type Extension } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { json } from '@codemirror/lang-json';
import { linter, lintGutter, type Diagnostic } from '@codemirror/lint';
import { indentWithTab } from '@codemirror/commands';
import { validate } from '../core/json';

/**
 * Above this size we stop running live validation on every change to keep the
 * main thread responsive. Formatting/validation on explicit button press still
 * works. (~2 MB.)
 */
export const LIVE_LINT_MAX_BYTES = 2_000_000;

/**
 * A CodeMirror linter that surfaces our core validation: the fatal syntax error
 * (as an error diagnostic) plus non-fatal advisories such as duplicate keys and
 * unsafe integers (as warnings), each anchored to a precise position.
 */
const jsonValidationLinter = linter(
  (view) => {
    const text = view.state.doc.toString();
    if (text.length > LIVE_LINT_MAX_BYTES) return [];
    const diagnostics: Diagnostic[] = [];
    const result = validate(text);
    const docLen = view.state.doc.length;
    if (result.error) {
      const from = Math.min(result.error.pos.offset, docLen);
      diagnostics.push({
        from,
        to: Math.min(from + 1, docLen),
        severity: 'error',
        message: result.error.message,
      });
    }
    for (const w of result.warnings) {
      const from = Math.min(w.pos.offset, docLen);
      diagnostics.push({
        from,
        to: Math.min(from + 1, docLen),
        severity: 'warning',
        message: w.message,
      });
    }
    return diagnostics;
  },
  { delay: 400 },
);

export interface EditorHandle {
  view: EditorView;
  getValue(): string;
  setValue(text: string): void;
}

export interface CreateEditorOptions {
  parent: HTMLElement;
  doc?: string;
  placeholder?: string;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  /** Include the live validation linter + gutter. Default true. */
  lint?: boolean;
}

/** Create a configured JSON editor. */
export function createEditor(options: CreateEditorOptions): EditorHandle {
  const { parent, doc = '', onChange, readOnly = false, lint = true } = options;

  const extensions: Extension[] = [
    basicSetup,
    json(),
    keymap.of([indentWithTab]),
    EditorView.lineWrapping,
  ];
  if (options.placeholder) extensions.push(cmPlaceholder(options.placeholder));
  if (lint) extensions.push(jsonValidationLinter, lintGutter());
  if (readOnly) {
    extensions.push(EditorState.readOnly.of(true));
    extensions.push(EditorView.editable.of(false));
  }
  if (onChange) {
    extensions.push(
      EditorView.updateListener.of((u) => {
        if (u.docChanged) onChange(u.state.doc.toString());
      }),
    );
  }

  const view = new EditorView({
    parent,
    state: EditorState.create({ doc, extensions }),
  });

  return {
    view,
    getValue: () => view.state.doc.toString(),
    setValue: (text: string) => {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: text },
      });
    },
  };
}